import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

const PROMO_PROPERTY = '_promo_gift';
const REWARD_PROPERTY = '_promo_reward';
const TIER_PROPERTY = '_promo_tier';
const PRODUCT_PROPERTY = '_promo_product_id';
const PROMO_MODE_ATTRIBUTE = '_promo_mode';
const PENDING_DISCOUNT_ATTRIBUTE = '_promo_pending_discount';
const PROMO_MODE_GIFTS = 'gifts';
const PROMO_MODE_DISCOUNT = 'discount';
const SECTION_ID = 'cart-drawer-section';
const GIFT_DISCOUNT_ERROR =
  'El descuento automático del regalo no está activo. El producto fue retirado para evitar cobrarlo en checkout.';

class CartPromoGifts {
  #queue = Promise.resolve();
  #scheduled = false;
  #busy = false;
  #activeReward = null;
  #persistentError = '';
  #allowCheckoutOnce = false;
  #observer = null;

  constructor() {
    document.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('change', this.#onChange);
    document.addEventListener('submit', this.#onCartSubmit, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartEvent);
    document.addEventListener('jm:promo-discount-request', this.#onDiscountRequest);

    const drawer = document.getElementById('cart-drawer');
    drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    if (drawer) {
      this.#observer = new MutationObserver((mutations) => {
        const promoWasAdded = mutations.some((mutation) =>
          Array.from(mutation.addedNodes).some(
            (node) =>
              node instanceof Element &&
              (node.matches('[data-jm-cart-promo]') || Boolean(node.querySelector('[data-jm-cart-promo]')))
          )
        );
        if (promoWasAdded && drawer.hasAttribute('open')) this.scheduleValidation();
      });
      this.#observer.observe(drawer, { childList: true, subtree: true });
    }

    this.scheduleValidation({ autoOpen: Boolean(drawer?.hasAttribute('open')) });
  }

  #root() {
    return document.querySelector('[data-jm-cart-promo]');
  }

  #config() {
    const script = this.#root()?.querySelector('[data-promo-config]');
    if (!(script instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('[cart-promo] Invalid promotion configuration.', error);
      return null;
    }
  }

  #onCartEvent = (event) => {
    if (event.promise) {
      event.promise.then(() => this.scheduleValidation()).catch(() => {});
    } else {
      this.scheduleValidation();
    }
  };

  #onDrawerOpen = () => {
    this.scheduleValidation();
  };

  #onDiscountRequest = (event) => {
    const code = String(event.detail?.code || '').trim();
    if (!code) return;
    this.#enqueue(async () => {
      const config = this.#config();
      if (!config) return;
      let cart = await this.#fetchCart();
      if (!this.#unlockedTiers(cart, config).length) return;

      cart = await this.#updateCartState(
        {
          attributes: {
            [PROMO_MODE_ATTRIBUTE]: '',
            [PENDING_DISCOUNT_ATTRIBUTE]: code,
          },
        },
        'update'
      );
      this.#renderState(cart, config);
      await this.#openPromotionChoice(cart, config);
    });
  };

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const close = event.target.closest('[data-promo-close]');
    if (close) {
      this.#modal()?.close();
      return;
    }

    const choiceClose = event.target.closest('[data-promo-choice-close]');
    if (choiceClose) {
      this.#choiceModal()?.close();
      return;
    }

    if (event.target.closest('[data-promo-choice-open]')) {
      this.#enqueue(() => this.#openPromotionChoice());
      return;
    }

    if (event.target.closest('[data-promo-choose-gifts]')) {
      this.#enqueue(() => this.#chooseGiftPromotion());
      return;
    }

    if (event.target.closest('[data-promo-choose-discount]')) {
      this.#enqueue(() => this.#chooseDiscountPromotion());
      return;
    }

    const submit = event.target.closest('[data-promo-submit]');
    if (submit) {
      this.#enqueue(() => this.#claimSelectedGift());
      return;
    }

    const change = event.target.closest('[data-promo-change]');
    if (change instanceof HTMLElement && change.dataset.promoChange) {
      this.#enqueue(() => this.#openModal(change.dataset.promoChange, false));
      return;
    }

    const swatch = event.target.closest('[data-promo-swatch]');
    if (swatch instanceof HTMLButtonElement && !swatch.disabled) {
      const select = this.#modal()?.querySelector(
        `[data-promo-option="${swatch.dataset.optionIndex}"]`
      );
      if (select instanceof HTMLSelectElement) {
        select.value = swatch.dataset.value || '';
        this.#syncVariantOptions();
      }
      return;
    }

    const tier = event.target.closest('[data-promo-reward]');
    if (tier instanceof HTMLElement) this.#openFromTier(tier);
  };

  #onKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const tier = event.target.closest('[data-promo-reward]');
    if (!tier || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    this.#openFromTier(/** @type {HTMLElement} */ (tier));
  };

  #onChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !event.target.matches('[data-promo-option]')) return;
    this.#syncVariantOptions();
  };

  #onCartSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.id !== 'cart-form') return;
    if (this.#allowCheckoutOnce) {
      this.#allowCheckoutOnce = false;
      return;
    }

    event.preventDefault();
    const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
    this.#enqueue(async () => {
      const result = await this.#validatePromotionalGifts();
      if (!result) return;

      if (result.unlockedTiers.length && result.incompatibleDiscounts.length && result.promoMode !== PROMO_MODE_DISCOUNT) {
        await this.#openPromotionChoice(result.cart, result.config);
        this.#showChoiceError('Elige una promoción antes de continuar al checkout.');
        return;
      }

      const unsafeGift = this.#giftLines(result.cart).some(
        (line) => Number(line.final_line_price) !== 0 || Number(line.quantity) !== 1
      );
      if (unsafeGift) {
        this.#showError('Revisamos tus regalos antes de checkout. Inténtalo nuevamente.');
        return;
      }

      const currentForm = document.getElementById('cart-form');
      if (!(currentForm instanceof HTMLFormElement)) return;
      const currentSubmitter = submitter?.id ? document.getElementById(submitter.id) : null;
      this.#allowCheckoutOnce = true;
      currentForm.requestSubmit(currentSubmitter instanceof HTMLElement ? currentSubmitter : undefined);
    });
  };

  scheduleValidation({ autoOpen = true } = {}) {
    if (this.#scheduled) return;
    this.#scheduled = true;

    window.setTimeout(() => {
      this.#scheduled = false;
      this.#enqueue(async () => {
        const result = await this.#validatePromotionalGifts();
        if (!autoOpen || !result) return;
        const choiceOpened = await this.#maybeOpenPromotionChoice(result);
        if (!choiceOpened) this.#maybeAutoOpen(result.cart, result.config);
      });
    }, 80);
  }

  #enqueue(operation) {
    this.#queue = this.#queue.then(operation, operation).catch((error) => {
      console.error('[cart-promo] Promotion operation failed.', error);
      this.#showError('No pudimos actualizar tu regalo. Inténtalo nuevamente.');
    });
    return this.#queue;
  }

  async #fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js?_promo_refresh=${Date.now()}`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  #isGift(item) {
    return String(item?.properties?.[PROMO_PROPERTY]) === 'true';
  }

  #eligibleSubtotal(cart) {
    return (cart.items || []).reduce((total, item) => {
      return this.#isGift(item) ? total : total + Number(item.final_line_price || 0);
    }, 0);
  }

  #discountIdentities(discount) {
    const application = discount?.discount_application || discount;
    return [application?.key, application?.title]
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase());
  }

  #giftDiscountIdentities(cart) {
    const identities = new Set();
    for (const gift of this.#giftLines(cart)) {
      for (const allocation of gift.line_level_discount_allocations || gift.discounts || []) {
        for (const identity of this.#discountIdentities(allocation)) identities.add(identity);
      }
    }
    return identities;
  }

  #isGiftDiscount(discount, giftDiscounts) {
    const identities = this.#discountIdentities(discount);
    return identities.length > 0 && identities.some((identity) => giftDiscounts.has(identity));
  }

  #incompatibleDiscounts(cart, config) {
    if (!config.blockDiscounts) return [];

    const found = new Map();
    const giftDiscounts = this.#giftDiscountIdentities(cart);
    const addDiscount = (discount, fallback) => {
      const application = discount?.discount_application || discount;
      if (application?.target_type === 'shipping_line') return;
      if (this.#isGiftDiscount(discount, giftDiscounts)) return;
      const label = String(application?.title || application?.code || fallback || 'Descuento activo').trim();
      const key = label.toLowerCase();
      if (key) found.set(key, { key, label });
    };

    for (const discount of cart.discount_codes || []) {
      if (discount.applicable !== false) addDiscount(discount, discount.code);
    }
    const pendingCode = String(cart.attributes?.[PENDING_DISCOUNT_ATTRIBUTE] || '').trim();
    if (pendingCode) addDiscount({ code: pendingCode }, pendingCode);
    for (const discount of cart.cart_level_discount_applications || []) addDiscount(discount);
    for (const item of cart.items || []) {
      if (this.#isGift(item)) continue;
      for (const discount of item.line_level_discount_allocations || item.discounts || []) addDiscount(discount);
    }

    return [...found.values()];
  }

  #hasIncompatibleDiscount(cart, config) {
    return this.#incompatibleDiscounts(cart, config).length > 0;
  }

  #promoMode(cart) {
    const mode = String(cart.attributes?.[PROMO_MODE_ATTRIBUTE] || '').toLowerCase();
    return mode === PROMO_MODE_GIFTS || mode === PROMO_MODE_DISCOUNT ? mode : '';
  }

  #selectionAttribute(reward) {
    return `_promo_${reward}_variant`;
  }

  #unlockedTiers(cart, config) {
    const eligibleSubtotal = this.#eligibleSubtotal(cart);
    return (config.tiers || []).filter(
      (tier) => tier.product && eligibleSubtotal >= Number(tier.threshold)
    );
  }

  #rememberGiftVariants(cart, attributes = {}) {
    for (const line of this.#giftLines(cart)) {
      const reward = line.properties?.[REWARD_PROPERTY];
      if (reward) attributes[this.#selectionAttribute(reward)] = String(line.variant_id);
    }
    return attributes;
  }

  #giftLines(cart, reward) {
    return (cart.items || []).filter(
      (item) => this.#isGift(item) && (!reward || item.properties?.[REWARD_PROPERTY] === reward)
    );
  }

  async #waitForFreeGift(reward, variantId) {
    let cart = null;
    let line = null;

    // Shopify can return the newly-added line before an automatic discount is
    // reflected by cart.js. Recalculate once, then retry briefly before the
    // safety check removes a gift that would otherwise be charged.
    for (let attempt = 0; attempt < 6; attempt += 1) {
      cart = await this.#fetchCart();
      line = this.#giftLines(cart, reward).find(
        (item) => String(item.variant_id) === String(variantId)
      );

      if (line && Number(line.final_line_price) === 0) return { cart, line };

      if ((attempt === 0 || attempt === 2) && line) {
        cart = await this.#updateCart({ [line.key]: 1 }, 'update', false);
        line = this.#giftLines(cart, reward).find(
          (item) => String(item.variant_id) === String(variantId)
        );
        if (line && Number(line.final_line_price) === 0) return { cart, line };
      }

      if (attempt < 5) {
        await new Promise((resolve) => window.setTimeout(resolve, 200 * (attempt + 1)));
      }
    }

    return { cart, line };
  }

  async #validatePromotionalGifts() {
    const config = this.#config();
    if (!config) return null;

    let cart = await this.#fetchCart();
    let promoMode = this.#promoMode(cart);
    let incompatibleDiscounts = this.#incompatibleDiscounts(cart, config);

    // A discount choice must not keep the customer locked after that discount
    // has actually disappeared from the current Shopify cart.
    if (promoMode === PROMO_MODE_DISCOUNT && incompatibleDiscounts.length === 0) {
      cart = await this.#updateCartState(
        { attributes: { [PROMO_MODE_ATTRIBUTE]: '' } },
        'update',
        false
      );
      promoMode = '';
    }

    if (this.#persistentError === GIFT_DISCOUNT_ERROR) this.#persistentError = '';

    let eligibleSubtotal = this.#eligibleSubtotal(cart);
    const structuralUpdates = {};
    const rememberedAttributes = {};

    for (const tier of config.tiers || []) {
      const lines = this.#giftLines(cart, tier.reward);
      const allowed = promoMode !== PROMO_MODE_DISCOUNT && eligibleSubtotal >= Number(tier.threshold);
      const expectedProductId = String(tier.product?.id || '');
      let keptGift = false;

      for (const line of lines) {
        const isConfiguredProduct = expectedProductId && String(line.product_id) === expectedProductId;
        const canKeepStructure = allowed && isConfiguredProduct && !keptGift;

        if (!canKeepStructure) {
          structuralUpdates[line.key] = 0;
          rememberedAttributes[this.#selectionAttribute(tier.reward)] = String(line.variant_id);
          continue;
        }

        keptGift = true;
        if (Number(line.quantity) !== 1) structuralUpdates[line.key] = 1;
      }
    }

    // Remove forged or obsolete promotional lines that no longer map to a configured reward.
    const configuredRewards = new Set((config.tiers || []).map((tier) => tier.reward));
    for (const line of this.#giftLines(cart)) {
      if (!configuredRewards.has(line.properties?.[REWARD_PROPERTY])) structuralUpdates[line.key] = 0;
    }

    // First remove gifts that are no longer structurally eligible. This lets
    // Shopify switch cleanly between the 2999 and 1999 automatic discounts
    // before we judge the price of a gift that is still allowed.
    if (Object.keys(structuralUpdates).length) {
      cart = await this.#updateCartState(
        {
          updates: structuralUpdates,
          ...(Object.keys(rememberedAttributes).length ? { attributes: rememberedAttributes } : {}),
        },
        'remove'
      );
      promoMode = this.#promoMode(cart);
      incompatibleDiscounts = this.#incompatibleDiscounts(cart, config);
      eligibleSubtotal = this.#eligibleSubtotal(cart);
    }

    // An eligible gift can briefly have its regular price while Shopify moves
    // from one Buy X Get Y tier to another. Confirm the settled server state
    // before treating that line as unsafe.
    for (const tier of config.tiers || []) {
      const allowed = promoMode !== PROMO_MODE_DISCOUNT && eligibleSubtotal >= Number(tier.threshold);
      if (!allowed) continue;
      const expectedProductId = String(tier.product?.id || '');
      const line = this.#giftLines(cart, tier.reward).find(
        (item) => String(item.product_id) === expectedProductId
      );
      if (line && Number(line.final_line_price) !== 0) {
        const settled = await this.#waitForFreeGift(tier.reward, line.variant_id);
        cart = settled.cart || cart;
      }
    }

    promoMode = this.#promoMode(cart);
    incompatibleDiscounts = this.#incompatibleDiscounts(cart, config);
    eligibleSubtotal = this.#eligibleSubtotal(cart);
    const unsafeUpdates = {};
    const unsafeAttributes = {};
    let confirmedDiscountFailure = false;

    for (const tier of config.tiers || []) {
      const allowed = promoMode !== PROMO_MODE_DISCOUNT && eligibleSubtotal >= Number(tier.threshold);
      const expectedProductId = String(tier.product?.id || '');
      let keptGift = false;

      for (const line of this.#giftLines(cart, tier.reward)) {
        const isConfiguredProduct = expectedProductId && String(line.product_id) === expectedProductId;
        const isActuallyFree = Number(line.final_line_price) === 0;
        const canKeep = allowed && isConfiguredProduct && isActuallyFree && !keptGift;
        if (canKeep) {
          keptGift = true;
          continue;
        }

        unsafeUpdates[line.key] = 0;
        unsafeAttributes[this.#selectionAttribute(tier.reward)] = String(line.variant_id);
        if (allowed && isConfiguredProduct && !isActuallyFree && incompatibleDiscounts.length === 0) {
          confirmedDiscountFailure = true;
        }
      }
    }

    if (Object.keys(unsafeUpdates).length) {
      cart = await this.#updateCartState(
        {
          updates: unsafeUpdates,
          ...(Object.keys(unsafeAttributes).length ? { attributes: unsafeAttributes } : {}),
        },
        'remove'
      );
      promoMode = this.#promoMode(cart);
      incompatibleDiscounts = this.#incompatibleDiscounts(cart, config);
    }

    if (confirmedDiscountFailure) this.#persistentError = GIFT_DISCOUNT_ERROR;

    this.#renderState(cart, config);
    return {
      cart,
      config,
      promoMode,
      incompatibleDiscounts,
      unlockedTiers: this.#unlockedTiers(cart, config),
    };
  }

  #renderState(cart, config) {
    const root = this.#root();
    if (!root) return;

    const eligibleSubtotal = this.#eligibleSubtotal(cart);
    const promoMode = this.#promoMode(cart);
    const incompatibleDiscounts = this.#incompatibleDiscounts(cart, config);
    const hasConflict = incompatibleDiscounts.length > 0;
    const unlockedTiers = this.#unlockedTiers(cart, config);
    const tiers = Array.from(root.querySelectorAll('[data-promo-tier]'))
      .map((element) => ({ element, threshold: Number(element.dataset.threshold) }))
      .sort((a, b) => a.threshold - b.threshold);
    const lastThreshold = tiers.at(-1)?.threshold || 0;
    const percentage = lastThreshold ? Math.min(100, Math.max(0, (eligibleSubtotal / lastThreshold) * 100)) : 0;
    root.style.setProperty('--jm-cart-progress', `${percentage}%`);
    root.dataset.eligibleSubtotal = String(eligibleSubtotal);

    let nextTier = null;
    for (const tier of tiers) {
      const unlocked = eligibleSubtotal >= tier.threshold;
      const isNext = !unlocked && !nextTier;
      if (isNext) nextTier = tier;
      tier.element.classList.toggle('is-unlocked', unlocked);
      tier.element.classList.toggle('is-next', isNext);

      const reward = tier.element.dataset.promoReward;
      if (reward) {
        const claimed = this.#giftLines(cart, reward).some((line) => Number(line.final_line_price) === 0);
        const canClaim = unlocked && promoMode !== PROMO_MODE_DISCOUNT && !hasConflict;
        tier.element.dataset.promoState = !unlocked
          ? 'locked'
          : claimed
            ? 'claimed'
            : canClaim
              ? 'unclaimed'
              : 'unavailable';
        tier.element.setAttribute('aria-disabled', canClaim ? 'false' : 'true');
        if (!unlocked) sessionStorage.removeItem(`jm-promo-seen:${config.cartToken}:${reward}`);
      }
    }

    const message = root.querySelector('[data-promo-message]');
    if (message) {
      message.replaceChildren();
      if (nextTier) {
        const label = nextTier.element.querySelector('.jm-cart-promo__label')?.textContent?.trim() || 'tu regalo';
        message.append('¡Estás a ');
        const amount = document.createElement('strong');
        amount.textContent = this.#formatMoney(nextTier.threshold - eligibleSubtotal, config.currency);
        message.append(amount, ' de desbloquear ');
        const reward = document.createElement('strong');
        reward.textContent = `${label.toUpperCase()}!`;
        message.append(reward);
      } else {
        const complete = document.createElement('strong');
        complete.textContent = '¡Felicidades! Desbloqueaste todos tus regalos.';
        message.append(complete);
      }
    }

    const notice = root.querySelector('[data-promo-notice]');
    if (notice instanceof HTMLElement) {
      let stateMessage = '';
      if (unlockedTiers.length && hasConflict && promoMode !== PROMO_MODE_DISCOUNT) {
        stateMessage = 'Tus regalos no pueden combinarse con el descuento actual. Elige el beneficio que prefieras.';
      } else if (unlockedTiers.length && promoMode === PROMO_MODE_DISCOUNT) {
        stateMessage = 'Estás usando tu descuento. También tienes regalos disponibles por tu compra.';
      }
      const text = this.#persistentError || stateMessage;
      notice.textContent = text;
      notice.hidden = !text;
    }

    const changePromotion = root.querySelector('[data-promo-choice-open]');
    if (changePromotion instanceof HTMLElement) {
      changePromotion.hidden = !unlockedTiers.length || (!hasConflict && promoMode !== PROMO_MODE_DISCOUNT);
    }
  }

  #formatMoney(cents, currency = 'MXN') {
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(cents) / 100);
    } catch (_error) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  }

  #openFromTier(tierElement) {
    if (tierElement.getAttribute('aria-disabled') === 'true' || !tierElement.classList.contains('is-unlocked')) return;
    const reward = tierElement.dataset.promoReward;
    if (reward) this.#enqueue(() => this.#openModal(reward, false));
  }

  #modal() {
    const modal = this.#root()?.querySelector('[data-promo-modal]');
    return modal instanceof HTMLDialogElement ? modal : null;
  }

  #choiceModal() {
    const modal = this.#root()?.querySelector('[data-promo-choice-modal]');
    return modal instanceof HTMLDialogElement ? modal : null;
  }

  async #maybeOpenPromotionChoice(result) {
    if (
      !result.unlockedTiers.length ||
      !result.incompatibleDiscounts.length ||
      result.promoMode === PROMO_MODE_DISCOUNT
    ) {
      return false;
    }

    return this.#openPromotionChoice(result.cart, result.config);
  }

  async #openPromotionChoice(existingCart = null, existingConfig = null) {
    const config = existingConfig || this.#config();
    const modal = this.#choiceModal();
    if (!config || !modal) return false;

    const cart = existingCart || await this.#fetchCart();
    const unlockedTiers = this.#unlockedTiers(cart, config);
    const incompatibleDiscounts = this.#incompatibleDiscounts(cart, config);
    if (!unlockedTiers.length || !incompatibleDiscounts.length) {
      this.#renderState(cart, config);
      return false;
    }

    const giftList = modal.querySelector('[data-promo-choice-gifts]');
    if (giftList) {
      giftList.replaceChildren();
      for (const tier of unlockedTiers) {
        const item = document.createElement('li');
        item.textContent = String(tier.label || tier.product?.title || 'Regalo gratis').toUpperCase();
        giftList.append(item);
      }
    }

    const discountLabel = modal.querySelector('[data-promo-choice-discount]');
    if (discountLabel) {
      discountLabel.textContent = incompatibleDiscounts.map((discount) => discount.label).join(' + ');
    }

    this.#showChoiceError('');
    this.#modal()?.close();
    if (!modal.open) modal.showModal();
    return true;
  }

  async #chooseGiftPromotion() {
    if (this.#busy) return;
    const config = this.#config();
    if (!config) return;

    this.#busy = true;
    this.#showChoiceError('');
    try {
      let cart = await this.#fetchCart();
      if (!this.#unlockedTiers(cart, config).length) {
        this.#choiceModal()?.close();
        this.#renderState(cart, config);
        return;
      }

      this.#choiceModal()?.close();
      cart = await this.#updateCartState(
        {
          discount: '',
          attributes: {
            [PROMO_MODE_ATTRIBUTE]: PROMO_MODE_GIFTS,
            [PENDING_DISCOUNT_ATTRIBUTE]: '',
          },
        },
        'update'
      );

      const remainingDiscounts = this.#incompatibleDiscounts(cart, config);
      if (remainingDiscounts.length) {
        await this.#openPromotionChoice(cart, config);
        this.#showChoiceError(
          'Shopify mantiene un descuento automático que no puede desactivarse desde el carrito. Elige usar el descuento o configura una Shopify Function para controlar esta elección.'
        );
        return;
      }

      this.#persistentError = '';
      const result = await this.#validatePromotionalGifts();
      if (result) this.#maybeAutoOpen(result.cart, result.config);
    } catch (error) {
      console.error('[cart-promo] Unable to select gift promotion.', error);
      await this.#openPromotionChoice();
      this.#showChoiceError('No pudimos activar tus regalos. Inténtalo nuevamente.');
    } finally {
      this.#busy = false;
    }
  }

  async #chooseDiscountPromotion() {
    if (this.#busy) return;
    const config = this.#config();
    if (!config) return;

    this.#busy = true;
    this.#showChoiceError('');
    try {
      const cart = await this.#fetchCart();
      const pendingCode = String(cart.attributes?.[PENDING_DISCOUNT_ATTRIBUTE] || '').trim();
      const discountCodes = (cart.discount_codes || [])
        .filter((discount) => discount.applicable !== false && discount.code)
        .map((discount) => String(discount.code));
      if (pendingCode && !discountCodes.some((code) => code.toLowerCase() === pendingCode.toLowerCase())) {
        discountCodes.push(pendingCode);
      }
      const updates = Object.fromEntries(this.#giftLines(cart).map((line) => [line.key, 0]));
      const attributes = this.#rememberGiftVariants(cart, {
        [PROMO_MODE_ATTRIBUTE]: PROMO_MODE_DISCOUNT,
        [PENDING_DISCOUNT_ATTRIBUTE]: '',
      });

      this.#choiceModal()?.close();
      let updatedCart = await this.#updateCartState(
        {
          ...(Object.keys(updates).length ? { updates } : {}),
          ...(pendingCode ? { discount: discountCodes.join(',') } : {}),
          attributes,
        },
        Object.keys(updates).length ? 'remove' : 'update'
      );

      const pendingApplied = !pendingCode || (updatedCart.discount_codes || []).some(
        (discount) =>
          String(discount.code || '').toLowerCase() === pendingCode.toLowerCase() &&
          discount.applicable !== false
      );
      if (!pendingApplied) {
        updatedCart = await this.#updateCartState(
          {
            attributes: {
              [PROMO_MODE_ATTRIBUTE]: '',
              [PENDING_DISCOUNT_ATTRIBUTE]: '',
            },
          },
          'update'
        );
        this.#persistentError = `El código ${pendingCode} no es válido para este carrito.`;
        this.#renderState(updatedCart, config);
        return;
      }

      this.#persistentError = '';
      this.#renderState(updatedCart, config);
    } catch (error) {
      console.error('[cart-promo] Unable to select discount promotion.', error);
      await this.#openPromotionChoice();
      this.#showChoiceError('No pudimos activar tu descuento. Inténtalo nuevamente.');
    } finally {
      this.#busy = false;
    }
  }

  async #openModal(reward, automatic) {
    const config = this.#config();
    if (!config) return;
    const tier = config.tiers?.find((entry) => entry.reward === reward);
    const modal = this.#modal();
    if (!tier?.product || !modal) {
      this.#showError(`Configura el producto de ${tier?.label || 'este regalo'} en el editor del tema.`);
      return;
    }

    const cart = await this.#fetchCart();
    const eligible = this.#eligibleSubtotal(cart) >= Number(tier.threshold);
    if (
      !eligible ||
      this.#promoMode(cart) === PROMO_MODE_DISCOUNT ||
      this.#hasIncompatibleDiscount(cart, config)
    ) {
      this.#renderState(cart, config);
      return;
    }

    const currentLine = this.#giftLines(cart, reward)[0];
    const savedVariantId = cart.attributes?.[this.#selectionAttribute(reward)];
    const currentVariantId = currentLine ? String(currentLine.variant_id) : String(savedVariantId || '');
    this.#activeReward = reward;

    const title = modal.querySelector('[data-promo-title]');
    const eyebrow = modal.querySelector('[data-promo-eyebrow]');
    const submit = modal.querySelector('[data-promo-submit]');
    const error = modal.querySelector('[data-promo-error]');
    if (title) title.textContent = String(tier.label || tier.product.title).toUpperCase();
    if (eyebrow) eyebrow.textContent = automatic ? '¡DESBLOQUEASTE UN REGALO!' : currentLine ? 'CAMBIA TU REGALO' : 'ELIGE TU REGALO';
    if (submit) submit.querySelector('.button-text').textContent = currentLine ? 'CAMBIAR GRATIS' : 'AGREGAR GRATIS';
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }

    this.#buildOptionSelectors(tier, currentVariantId);
    if (!modal.open) modal.showModal();
  }

  #buildOptionSelectors(tier, currentVariantId) {
    const container = this.#modal()?.querySelector('[data-promo-options]');
    if (!container) return;
    container.replaceChildren();

    const product = tier.product;
    const currentVariant = product.variants.find((variant) => String(variant.id) === currentVariantId);
    const onlyVariant = product.variants.length === 1 ? product.variants[0] : null;

    product.options.forEach((optionName, index) => {
      const isColor = this.#isColorOption(optionName);
      const field = document.createElement(isColor ? 'fieldset' : 'label');
      field.className = 'jm-promo-modal__field';
      const label = document.createElement(isColor ? 'legend' : 'span');
      label.textContent = optionName;
      if (isColor) {
        const selectedValue = document.createElement('span');
        selectedValue.className = 'jm-promo-modal__swatch-value';
        selectedValue.dataset.promoSwatchValue = String(index);
        label.append(' ', selectedValue);
      }
      const select = document.createElement('select');
      select.dataset.promoOption = String(index);
      select.className = 'jm-promo-modal__select';
      select.setAttribute('aria-label', optionName);
      select.hidden = isColor;

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = `Selecciona ${String(optionName).toLowerCase()}`;
      select.append(placeholder);

      const values = [...new Set(product.variants.map((variant) => variant.options[index]))];
      for (const value of values) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.append(option);
      }

      select.value = currentVariant?.options[index] || onlyVariant?.options[index] || '';
      field.append(label, select);

      if (isColor) {
        const swatches = document.createElement('div');
        swatches.className = 'jm-promo-modal__swatches';
        swatches.setAttribute('role', 'group');
        swatches.setAttribute('aria-label', optionName);

        for (const value of values) {
          const swatch = document.createElement('button');
          swatch.type = 'button';
          swatch.className = 'jm-promo-modal__swatch';
          swatch.dataset.promoSwatch = '';
          swatch.dataset.optionIndex = String(index);
          swatch.dataset.value = value;
          swatch.setAttribute('aria-label', value);
          swatch.title = value;

          const image = product.variants.find(
            (variant) => variant.options[index] === value && variant.image
          )?.image;
          if (image) {
            swatch.style.backgroundImage = `url("${String(image).replaceAll('"', '%22')}")`;
          } else {
            swatch.style.backgroundColor = this.#fallbackSwatchColor(value);
          }
          swatches.append(swatch);
        }
        field.append(swatches);
      }
      container.append(field);
    });

    this.#syncVariantOptions();
  }

  #syncVariantOptions() {
    const config = this.#config();
    const tier = config?.tiers?.find((entry) => entry.reward === this.#activeReward);
    const modal = this.#modal();
    if (!tier?.product || !modal) return;

    const selects = Array.from(modal.querySelectorAll('[data-promo-option]'));
    const values = selects.map((select) => select.value);

    for (const select of selects) {
      const index = Number(select.dataset.promoOption);
      for (const option of Array.from(select.options)) {
        if (!option.value) continue;
        option.disabled = !tier.product.variants.some((variant) => {
          if (!variant.available || variant.options[index] !== option.value) return false;
          return values.every((value, otherIndex) => {
            return otherIndex === index || !value || variant.options[otherIndex] === value;
          });
        });
      }
    }

    for (const swatch of modal.querySelectorAll('[data-promo-swatch]')) {
      if (!(swatch instanceof HTMLButtonElement)) continue;
      const index = Number(swatch.dataset.optionIndex);
      const select = selects.find((entry) => Number(entry.dataset.promoOption) === index);
      const option = select?.querySelector(`option[value="${CSS.escape(swatch.dataset.value || '')}"]`);
      const selected = select?.value === swatch.dataset.value;
      swatch.disabled = option instanceof HTMLOptionElement ? option.disabled : true;
      swatch.classList.toggle('is-selected', selected);
      swatch.setAttribute('aria-pressed', selected ? 'true' : 'false');
    }

    for (const selectedLabel of modal.querySelectorAll('[data-promo-swatch-value]')) {
      if (!(selectedLabel instanceof HTMLElement)) continue;
      const select = selects.find(
        (entry) => entry.dataset.promoOption === selectedLabel.dataset.promoSwatchValue
      );
      selectedLabel.textContent = select?.value ? `— ${select.value}` : '';
    }

    const selectedVariant = tier.product.variants.find(
      (variant) => variant.available && variant.options.every((value, index) => value === values[index])
    );
    const submit = modal.querySelector('[data-promo-submit]');
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = !selectedVariant || this.#busy;
      submit.dataset.variantId = selectedVariant ? String(selectedVariant.id) : '';
    }
  }

  #isColorOption(optionName) {
    return /color|colour|tono/i.test(String(optionName));
  }

  #fallbackSwatchColor(value) {
    const normalized = String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const colors = {
      negro: '#161616',
      blanco: '#f7f7f3',
      gris: '#858585',
      'gris claro': '#c9c9c9',
      cafe: '#815b43',
      beige: '#d8c3a5',
      chocolate: '#55352a',
      azul: '#315ea8',
      'azul marino': '#172947',
      'azul cielo': '#86bde7',
      rojo: '#bd2734',
      tinto: '#6f1d32',
      rosa: '#e9a5bf',
      lila: '#ac88c5',
      purple: '#725091',
      uva: '#68436f',
      verde: '#4f8057',
      amarillo: '#e5c84f',
      naranja: '#dc783c',
      mamey: '#e58a67',
    };
    const exact = colors[normalized];
    if (exact) return exact;
    const partial = Object.entries(colors).find(([name]) => normalized.includes(name));
    return partial?.[1] || '#d8d8d8';
  }

  async #claimSelectedGift() {
    if (this.#busy || !this.#activeReward) return;
    const config = this.#config();
    const tier = config?.tiers?.find((entry) => entry.reward === this.#activeReward);
    const submit = this.#modal()?.querySelector('[data-promo-submit]');
    const variantId = submit instanceof HTMLButtonElement ? submit.dataset.variantId : '';
    const variant = tier?.product?.variants?.find((entry) => String(entry.id) === variantId && entry.available);
    if (!config || !tier?.product || !variant) return;

    this.#busy = true;
    this.#syncVariantOptions();
    this.#showModalError('');
    if (this.#persistentError === GIFT_DISCOUNT_ERROR) this.#persistentError = '';

    try {
      let cart = await this.#fetchCart();
      const eligible = this.#eligibleSubtotal(cart) >= Number(tier.threshold);
      if (
        !eligible ||
        this.#promoMode(cart) === PROMO_MODE_DISCOUNT ||
        this.#hasIncompatibleDiscount(cart, config)
      ) {
        this.#showModalError('Tu carrito ya no cumple las condiciones de este regalo.');
        this.#renderState(cart, config);
        return;
      }

      const existing = this.#giftLines(cart, tier.reward);
      if (existing.length === 1 && String(existing[0].variant_id) === String(variant.id) && existing[0].quantity === 1) {
        this.#modal()?.close();
        return;
      }

      // Remove the previous variant first so the discount never has to choose between two copies.
      if (existing.length) {
        const removals = Object.fromEntries(existing.map((line) => [line.key, 0]));
        cart = await this.#updateCart(removals, 'remove', false);
      }

      let addedLine = null;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        await this.#postCart(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          items: [
            {
              id: Number(variant.id),
              quantity: 1,
              properties: {
                [PROMO_PROPERTY]: 'true',
                [REWARD_PROPERTY]: tier.reward,
                // Thresholds are stored in cents internally, while the private
                // cart property intentionally uses the human currency amount.
                [TIER_PROPERTY]: String(Math.round(Number(tier.threshold) / 100)),
                [PRODUCT_PROPERTY]: String(tier.product.id),
              },
            },
          ],
        });

        const discountedGift = await this.#waitForFreeGift(tier.reward, variant.id);
        cart = discountedGift.cart;
        addedLine = discountedGift.line;
        if (addedLine && Number(addedLine.final_line_price) === 0) break;

        if (addedLine) {
          cart = await this.#updateCart({ [addedLine.key]: 0 }, 'remove', false);
          addedLine = null;
        }

        if (attempt === 0) {
          await new Promise((resolve) => window.setTimeout(resolve, 350));
          cart = await this.#fetchCart();
          const stillEligible = this.#eligibleSubtotal(cart) >= Number(tier.threshold);
          if (
            !stillEligible ||
            this.#promoMode(cart) === PROMO_MODE_DISCOUNT ||
            this.#hasIncompatibleDiscount(cart, config)
          ) {
            break;
          }
        }
      }

      if (!addedLine || Number(addedLine.final_line_price) !== 0) {
        if (addedLine) await this.#updateCart({ [addedLine.key]: 0 }, 'remove', false);
        this.#persistentError = GIFT_DISCOUNT_ERROR;
        throw new Error('Promotional variant was not discounted to zero.');
      }

      this.#persistentError = '';
      this.#modal()?.close();
      // Render from the settled cart instead of the immediate add response,
      // whose sections can still contain the pre-discount price.
      await this.#refreshAfterMutation(cart, null, 'add');
      const validated = await this.#validatePromotionalGifts();
      if (validated) this.#maybeAutoOpen(validated.cart, validated.config);
    } catch (error) {
      console.error('[cart-promo] Gift claim failed.', error);
      this.#showModalError(
        this.#persistentError || 'No pudimos agregar este regalo. Verifica la disponibilidad e inténtalo nuevamente.'
      );
      await this.#refreshAfterMutation(await this.#fetchCart(), null, 'remove');
    } finally {
      this.#busy = false;
      this.#syncVariantOptions();
    }
  }

  async #postCart(url, payload) {
    const sectionIds = this.#sectionIds();
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: sectionIds.join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || `Cart mutation failed (${response.status})`);
    return data;
  }

  async #updateCart(updates, action = 'update', refresh = true) {
    return this.#updateCartState({ updates }, action, refresh);
  }

  async #updateCartState(payload, action = 'update', refresh = true) {
    const data = await this.#postCart(`${window.Shopify?.routes?.root || '/'}cart/update.js`, payload);
    const cart = data.items ? data : await this.#fetchCart();
    if (refresh) await this.#refreshAfterMutation(cart, data.sections, action);
    return cart;
  }

  #sectionIds() {
    const ids = new Set([SECTION_ID]);
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (component instanceof HTMLElement && component.dataset.sectionId) ids.add(component.dataset.sectionId);
    }
    return [...ids];
  }

  async #refreshAfterMutation(cart, sections, action) {
    if (sections) {
      for (const [sectionId, html] of Object.entries(sections)) {
        if (html && document.getElementById(`shopify-section-${sectionId}`)) {
          morphSection(sectionId, html, {
            mode: sectionId === SECTION_ID ? 'hydration' : 'full',
          });
        }
      }
    } else {
      for (const sectionId of this.#sectionIds()) {
        if (document.getElementById(`shopify-section-${sectionId}`)) {
          await sectionRenderer.renderSection(sectionId, {
            cache: false,
            mode: sectionId === SECTION_ID ? 'hydration' : 'full',
          });
        }
      }
    }

    const promise = Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        source: 'cart-promo-gifts',
        didError: false,
      },
    });
    const eventTarget = document.querySelector(`cart-items-component[data-section-id="${SECTION_ID}"]`) || document;
    eventTarget.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [],
        promise,
      })
    );
  }

  #maybeAutoOpen(cart, config) {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer?.hasAttribute('open') || this.#modal()?.open || this.#busy) return;
    if (this.#promoMode(cart) === PROMO_MODE_DISCOUNT || this.#hasIncompatibleDiscount(cart, config)) return;

    const eligibleSubtotal = this.#eligibleSubtotal(cart);
    const nextReward = (config.tiers || [])
      .slice()
      .sort((a, b) => Number(a.threshold) - Number(b.threshold))
      .find((tier) => {
        if (eligibleSubtotal < Number(tier.threshold) || this.#giftLines(cart, tier.reward).length) return false;
        const key = `jm-promo-seen:${config.cartToken}:${tier.reward}`;
        return sessionStorage.getItem(key) !== 'true';
      });
    if (!nextReward) return;

    const key = `jm-promo-seen:${config.cartToken}:${nextReward.reward}`;
    sessionStorage.setItem(key, 'true');
    window.setTimeout(() => this.#enqueue(() => this.#openModal(nextReward.reward, true)), 180);
  }

  #showError(message) {
    this.#persistentError = message;
    const notice = this.#root()?.querySelector('[data-promo-notice]');
    if (notice instanceof HTMLElement) {
      notice.textContent = message;
      notice.hidden = !message;
    }
  }

  #showModalError(message) {
    const error = this.#modal()?.querySelector('[data-promo-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  #showChoiceError(message) {
    const error = this.#choiceModal()?.querySelector('[data-promo-choice-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }
}

if (!window.__jmCartPromoGifts) {
  window.__jmCartPromoGifts = new CartPromoGifts();
}
